/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */
import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import { isObjectOrArrayAndEmpty } from '../../../shared/Utils/Utils';
import useConnectorAndDynamicProps from '../../../shared/useConnectorAndDynamicProps/useConnectorAndDynamicProps';
import ConnectionError from '../../../shared/ErrorComponents/ConnectionError';
import withLoadingStatus from '../../../shared/StatusWrappers/LoadingStatus';
import withErrorStatus from '../../../shared/StatusWrappers/ErrorStatus';
import useMounted from '../../../shared/useMount/useMount';
import EmaasEnergyUsageGraphComponent from '../EmaasEnergyUsageGraphComponent/EmaasEnergyUsageGraphComponent';
import {
  IAny,
  IEmaasEnergyUsageComponentDataArea,
  IEmaasEnergyUsageComponentDataAreaV2,
  IEmaasEnergyUsageHOCProps,
  ISiteCard
} from '../../IEnergyManagementProps';
import FilterScope1Action from '../../../../context/FilterScope1Context/FilterScope1Actions';
import { useInterval } from '../../../shared/useInterval/useInterval';
import {
  POLLING_MILLI_SECONDS,
  POLLING_SECONDS
} from '../../../shared/Utils/Constants';
import EmaasBasicSubscriptionEnergyUsageComponent from '../EmaasBasicSubEnergyUsageGraphComponent/EmaasBasicSubEnergyUsageGraphComponent';
import { EnergyUsageField } from '../../../shared/Enums';
import {
  getEnergyEndDateBasedOnSub,
  getEnergyStartDate
} from '../../../shared/Utils/EnergyUsagesViewDetailsUtils';
import { PollingContext } from '../../../../context/PollingContext/PollingContext';
import { getIntervalType } from '../../../shared/Utils/PortfolioEnergy/PortfolioEnergyVDUtils';
import { useFilterScope1Context } from '../../../../context/FilterScope1Context/FilterScope1Context';
import EmaasNILMEnergyUsageGraphComponent from '../EmaasNILMEnergyUsageGraphComponent/EmaasNILMEnergyUsageGraphComponent';

interface IEmaasEnergyUsageGraphKPIHOCContentContent extends ISiteCard {
  data:
    | IEmaasEnergyUsageComponentDataArea
    | IEmaasEnergyUsageComponentDataAreaV2;
  emaasContext?: IAny;
}

const EmaasEnergyUsageGraphKPIHOCContent = (
  props: IEmaasEnergyUsageGraphKPIHOCContentContent
) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const { data, onRefresh } = props;

  if (isObjectOrArrayAndEmpty(data)) {
    return (
      <div className="errorDisplay common-error-container">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={onRefresh}
        />
      </div>
    );
  }

  return (
    <>
      {data.subscriptionType === EnergyUsageField.BASICSUBS ? (
        data?.addOn?.includes('NILM') ? (
          <EmaasNILMEnergyUsageGraphComponent
            data={data as IEmaasEnergyUsageComponentDataAreaV2}
            viewType={EnergyUsageField.NILM}
          />
        ) : (
          <EmaasBasicSubscriptionEnergyUsageComponent
            data={data}
            viewType={EnergyUsageField.SITEENERGY}
          />
        )
      ) : (
        <EmaasEnergyUsageGraphComponent data={data} />
      )}
    </>
  );
};

const EmaasEnergyUsageGraphKPIHOCWithLoader = withLoadingStatus(
  withErrorStatus(EmaasEnergyUsageGraphKPIHOCContent)
);

// Refresh every hour
const ENERGYUSAGE_REFRESH_INTERVAL = POLLING_SECONDS * POLLING_MILLI_SECONDS;

const EmaasEnergyUsageGraphKPIHOC = (props: IEmaasEnergyUsageHOCProps) => {
  const { requestContext, emaasContext } = props;

  const startDate = getEnergyStartDate(
    requestContext.context.startTime!,
    requestContext.context.subscriptionType!,
    emaasContext
  );
  const endDate = getEnergyEndDateBasedOnSub(
    requestContext.context.subscriptionType!,
    requestContext.context.endTime!,
    requestContext.context.startTime!,
    emaasContext
  );
  const connectorConfig = {
    name: 'emaas-energy-usage',
    options: {
      startDate: startDate,
      endDate: endDate,
      allDetails: 'true',
      intervalType: getIntervalType(startDate, endDate)
    }
  };
  const [silentLoading, setIsSilentLoading] = useState<boolean>(false);
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const [pollingCount, setPollingCount] = useState(0);

  const isMounted = useMounted();

  const [dataConnector] = useConnectorAndDynamicProps({
    connectorConfig,
    requestContext
  });

  const onRefresh = () => dataConnector?.actions?.getAll();

  useEffect(() => {
    setIsSilentLoading(false);
    if (isMounted) {
      onRefresh();
    }
  }, [requestContext]);

  useEffect(() => {
    if (isMounted) {
      onRefresh();
    }
  }, [pollingCount]);

  const { all, loading, error, status } = dataConnector.data;

  const EmaasEnergyUsage = (all as unknown) as IEmaasEnergyUsageComponentDataArea;

  // Polling mechanism 15 min
  useInterval(() => {
    if (EmaasEnergyUsage?.subscriptionType === EnergyUsageField.PREMIUMSUBS) {
      setIsSilentLoading(true);
      setPollingCount(pollingCount + 1);
    }
  }, ENERGYUSAGE_REFRESH_INTERVAL);

  const removeSilentLoadingonRefresh = () => {
    setIsSilentLoading(false);
    return onRefresh();
  };

  const isDataAvailable = EmaasEnergyUsage ? true : false;
  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [statusCode, setStatusCode] = useState<number | null>(status);
  const [errorInfo, setErrorInfo] = useState(error);
  const [data, setData] = useState({} as IEmaasEnergyUsageComponentDataArea);
  const [state, dispatch] = useFilterScope1Context();
  const pollingContext = useContext(PollingContext);
  const { setPollingTime } = pollingContext;

  useEffect(() => {
    setIsLoading(loading);
    if ((isDataAvailable || error || status) && !loading) {
      if (isDataAvailable) {
        setData(EmaasEnergyUsage);
        setPollingTime && setPollingTime();
        dispatch(
          FilterScope1Action.subscriptionValue(
            EmaasEnergyUsage.subscriptionType as IAny
          )
        );
      }
      setErrorInfo(error);
    }
    setStatusCode(status);
  }, [isDataAvailable, loading, status]);

  return (
    <EmaasEnergyUsageGraphKPIHOCWithLoader
      loading={isLoading}
      error={errorInfo}
      statusCode={statusCode}
      onRefresh={removeSilentLoadingonRefresh}
      data={data}
      loadingText={t(d.LOADING)}
      title={t(d.CONNECTIONERRORTITLE)}
      noDataMessage={t(d.NODATAAVAILABLE)}
      connectionErrorMessage={t(d.CONNECTIONERROR)}
      silentLoading={silentLoading}
      emaasContext={emaasContext}
    />
  );
};
export default EmaasEnergyUsageGraphKPIHOC;
